import { stateFullLabel, stateLabel } from './helpers'

/*
 * Die Zielgruppe einer Nachricht.
 *
 * Am Datensatz stehen fuenf Listen: roles, states, schoolType, companyTypes,
 * companyBranch. Eine leere Liste heisst nicht "niemand", sondern **keine
 * Einschraenkung** — die Nachricht gilt dann fuer alle. Sind alle fuenf leer,
 * gilt sie ausnahmslos fuer alle.
 *
 * Genau das ist die Angabe, die man beim Lesen einer Liste oder einer
 * Zeitachse braucht: Steht die Nachricht fuer alle da, oder nur fuer
 * Lehrkraefte, oder nur fuer ein Bundesland? Frueher stand die Antwort nur im
 * Fuss der Karte; die Zeitachse zeigte den Titel und sonst nichts.
 *
 * Beide Ansichten rechnen deshalb hier — eine Stelle, eine Reihenfolge, eine
 * Schreibweise.
 */

// Wie viele Werte in der Kurzfassung stehen, bevor "+N" uebernimmt. Drei
// passen neben einen Balken der Zeitachse, ohne die Zeile zu sprengen.
export const KURZ_MAX_WERTE = 3

/*
 * Schultypen tragen ihre Erklaerung in Klammern mit ("AHS (Allgemein bildende
 * hoehere Schule)"). In einer Klammer hinter dem Titel steht die dann in einer
 * Klammer in einer Klammer und ist dreimal so lang wie die Angabe daneben. Fuer
 * die Kurzfassung bleibt der Teil davor; ausgeschrieben steht der volle Name im
 * Hinweis.
 */
const kurzerSchultyp = wert =>
	String(wert)
		.replace(/\s*\(.*$/, '')
		.trim() || wert

/**
 * Die tatsaechlich gesetzten Einschraenkungen, in der Reihenfolge des Dialogs.
 *
 * Jede Facette traegt zwei Wertelisten: `values` fuer die Anzeige (Bundesland
 * abgekuerzt, siehe `stateLabel`) und `longValues` fuer den Hinweis beim
 * Darueberfahren, wo der volle Name hingehoert.
 *
 * Dazu `alle`: ob dieses Feld **saemtliche** zur Wahl stehenden Werte enthaelt.
 * Das kommt oft vor — im Dialog sind neun Schultypen und neunzehn Branchen mit
 * ein paar Klicks alle angehakt — und schraenkt in Wahrheit nichts ein. Fuer
 * die Beschriftung ist das der Unterschied zwischen "nur fuer Schueler:innen"
 * und "nur fuer Schueler:innen · Lehrer:innen · W +31".
 *
 * Dafuer braucht die Funktion `totals`: je Feld die Liste aller waehlbaren
 * Werte (`{ roles, states, schoolType, companyTypes, companyBranch }`). Fehlt
 * sie, ist `alle` immer falsch — die Angabe wird dann laenger, aber nie falsch.
 *
 * Leeres Ergebnis = die Nachricht gilt fuer alle.
 */
export function newsAudienceFacets(item, { t, branchLabel = value => value, totals }) {
	if (!item) return []

	// "Alle angehakt" heisst: jeder waehlbare Wert steht in der Liste.
	const deckAbAlles = (feld, werte) => {
		const alleWerte = totals?.[feld]
		if (!alleWerte?.length) return false

		return alleWerte.every(wert => werte.includes(wert))
	}

	return [
		item.roles?.length && {
			key: 'roles',
			label: t('User roles'),
			values: item.roles.map(role => t(role))
		},
		item.states?.length && {
			key: 'states',
			label: t('state'),
			values: item.states.map(stateLabel),
			longValues: item.states.map(stateFullLabel)
		},
		item.schoolType?.length && {
			key: 'schoolType',
			label: t('School Types'),
			values: item.schoolType.map(kurzerSchultyp),
			longValues: item.schoolType
		},
		item.companyTypes?.length && {
			key: 'companyTypes',
			label: t('Company type'),
			values: item.companyTypes
		},
		item.companyBranch?.length && {
			key: 'companyBranch',
			label: t('branch'),
			values: item.companyBranch.map(branchLabel)
		}
	]
		.filter(Boolean)
		.map(facet => ({
			longValues: facet.values,
			...facet,
			alle: deckAbAlles(facet.key, item[facet.key] || [])
		}))
}

/**
 * Die Zielgruppe in einem Halbsatz — fuer die Klammer hinter einem Titel.
 *
 * Ohne Einschraenkung "fuer alle", sonst "nur fuer …" mit den Werten selbst.
 * Die Namen der Felder ("Rollen", "Bundesland") bleiben weg: In der Klammer
 * waeren sie doppelt so lang wie die Angabe, und "nur fuer Lehrkraefte" sagt
 * ohnehin, worum es geht. Wer es genau wissen will, faehrt darueber
 * (`newsAudienceFull`).
 *
 * Felder mit allen Werten zaehlen nicht: Sie schraenken nichts ein. Ohne das
 * las sich der haeufigste Fall im Bestand — beide Rollen, ein Bundesland, alle
 * neun Schultypen, alle drei Unternehmensformen, alle neunzehn Branchen — als
 * "nur fuer Schueler:innen · Lehrer:innen · W +31". Die 31 sind kein Hinweis
 * auf eine engere Zielgruppe, sondern das Gegenteil.
 */
export function newsAudienceShort(facets, t, max = KURZ_MAX_WERTE) {
	const eng = (facets || []).filter(facet => !facet.alle)
	if (!eng.length) return t('for everyone')

	const werte = eng.flatMap(facet => facet.values)
	const gezeigt = werte.slice(0, max)
	const rest = werte.length - gezeigt.length

	return `${t('only for')} ${gezeigt.join(' · ')}${rest > 0 ? ` +${rest}` : ''}`
}

/**
 * Dieselbe Angabe ausgeschrieben, mit Feldnamen — fuer den Hinweistext.
 *
 * Hier stehen auch die Felder, die alles enthalten — aber als "Alle" und nicht
 * als Aufzaehlung: Neunzehn Branchen im Hinweis waeren eine Textwand, und die
 * Aussage ist ohnehin eine einzige.
 */
export function newsAudienceFull(facets, t) {
	if (!facets?.length) return t('Visible to all users')

	return facets.map(facet => `${facet.label}: ${facet.alle ? t('All') : facet.longValues.join(', ')}`).join(' · ')
}
